import { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { rankByRecommendations } from '@atlassian/search-client/rank-by-recommendations';
import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';

import { universalTypeFilterId } from '../../../../common/constants/filters/types';
import { ProductKeys } from '../../../../common/constants/product-keys';
import { useProductConfigs } from '../../../../common/constants/products';
import {
	type AppOption,
	buildNounOptions,
	buildProductOptions,
	splitByAuthStatus,
} from '../../../../common/utils/app-filter-options';
import { filterProductsForNouns } from '../../../../common/utils/filter-products-for-nouns';
import { getShouldShowNouns } from '../../../../common/utils/get-should-show-nouns';
import { useRightPanelRecommendations } from '../../../../controllers/recommened3pApps';
import { useBootstrap, useCurrentUserId } from '../../../../controllers/store/bootstrap';
import {
	useFilters,
	useProducts,
	useSelectedDatasource,
	useSelectedProduct,
} from '../../../../controllers/store/search';
import { applyAppPillLayout, type AppPillLayout, DEFAULT_APP_PILL_LAYOUT } from './app-pill-layout';
import { getLiteConnectedAppRanking, rankConnectedAppsForLite } from './connected-app-ranking';
import { usePromotedApps } from './promoted-apps';

/**
 * Builds the ordered list of visible and overflow app options for the Lite mode pill bar.
 * - Total of up to 9 visible pills, split per the {@link DEFAULT_APP_PILL_LAYOUT}.
 * - Unauthenticated 3P apps are ranked by recommended3pApps order.
 * - Remaining apps beyond the visible budget go to the "Show all apps" overflow dropdown.
 *
 * Uses shared util functions from app-filter-options for product/noun building and auth splitting,
 * and delegates final visible/overflow slicing to {@link applyAppPillLayout}.
 *
 * `layout` lets another surface reuse this option-building with a different visible
 * budget — the refreshed FPS filter row (QS-10724) passes a capped layout so it shows
 * a configurable number of icon-only app tiles. Pass a referentially stable layout.
 */
export function useLiteAppOptions(
	layout: AppPillLayout = DEFAULT_APP_PILL_LAYOUT,
	{ isRefreshedFilterRow = false }: { isRefreshedFilterRow?: boolean } = {},
): {
	visibleOptions: AppOption[];
	overflowOptions: AppOption[];
} {
	const [
		{ products: availableProducts, nouns: availableNouns, thirdPartyConfigs, primaryProduct },
	] = useBootstrap();
	const { availableProductsForSelectedSites } = useProducts();
	const [selectedProduct] = useSelectedProduct();
	const [selectedDatasource] = useSelectedDatasource();
	const userId = useCurrentUserId();
	const { recommended3pApps } = useRightPanelRecommendations({ userId });
	const filters = useFilters();
	const { promotedAppKeys, promoteApp, promotedWebCrawlerDatasource } = usePromotedApps();

	const { formatMessage } = useIntl();
	const products = useProductConfigs();

	// Derive the currently selected noun type from the universal type filter.
	// When a noun like "project" or "goal" is selected, the store sets product=townsquare
	// and sets the type filter to that noun value.
	const selectedNounType = useMemo(() => {
		const typeFilter = filters[universalTypeFilterId];
		if (typeFilter && 'selectedOptions' in typeFilter) {
			const selected = typeFilter.selectedOptions.find((opt) => opt.isSelected);
			return selected?.value;
		}
		return undefined;
	}, [filters]);

	const result = useMemo(() => {
		const productsForAppBar =
			serveFpsMultiSiteSearchExperiment({
				primaryProduct,
				fireExperimentExposure: false,
			}) !== 'control'
				? availableProductsForSelectedSites
				: availableProducts;

		// Always exclude Townsquare/Atlas (rendered as "Home") from the Lite app pill bar.
		// In the right rail, Atlas is hidden because it's classified as a "nested product" of
		// the `atlas` product category and the category's primary product key (`'atlas'`) is
		// not a real ProductKeys value, so the parent never renders. The Lite filter bar does
		// not implement that nested-categories logic, so we drop Atlas explicitly here to keep
		// behaviour consistent with the right rail.
		const productsWithoutAtlas = productsForAppBar.filter((key) => key !== ProductKeys.Atlas);

		const shouldShowNouns = getShouldShowNouns(availableNouns, productsWithoutAtlas);
		const filteredProducts = filterProductsForNouns(productsWithoutAtlas, shouldShowNouns);

		const productOptions = buildProductOptions({
			filteredProducts,
			products,
			selectedProduct,
			thirdPartyConfigs,
		});

		const nounOptions = shouldShowNouns
			? buildNounOptions({
					availableNouns,
					availableProducts: productsWithoutAtlas,
					selectedProduct,
					selectedNounType,
					formatMessage,
				})
			: [];

		const allOptions = [...productOptions, ...nounOptions];
		const { authenticatedOptions: authenticatedRaw, unauthenticatedOptions: unauthenticatedRaw } =
			splitByAuthStatus(allOptions, thirdPartyConfigs);

		// Surface the specific web crawler website's name on the pill instead of the generic
		// "Web pages" noun label — so the pill reads e.g. "Atlassian Developer". Prefer the live
		// selection, but fall back to the last-selected website (`promotedWebCrawlerDatasource`) so
		// the name persists after the user selects a different app filter. Only when no website has
		// been chosen at all is the generic noun label kept.
		const activeWebCrawlerDatasource =
			selectedProduct === ProductKeys.AtlassianWebCrawler && selectedDatasource
				? selectedDatasource
				: promotedWebCrawlerDatasource;
		const selectedWebCrawlerName = activeWebCrawlerDatasource
			? activeWebCrawlerDatasource.workspaceName || activeWebCrawlerDatasource.workspaceUrl
			: undefined;
		const relabelSelectedWebCrawler = (option: AppOption): AppOption =>
			selectedWebCrawlerName && option.value === ProductKeys.AtlassianWebCrawler
				? { ...option, label: selectedWebCrawlerName }
				: option;
		const authenticatedOptions = authenticatedRaw.map(relabelSelectedWebCrawler);
		const unauthenticatedOptions = unauthenticatedRaw.map(relabelSelectedWebCrawler);

		const rankedAuthenticatedOptions = rankConnectedAppsForLite(
			authenticatedOptions,
			getLiteConnectedAppRanking(primaryProduct, { isRefreshedFilterRow }),
		);

		// Rank unauthenticated 3P by recommendations order: walk the recommended list first
		// (preserving rank), then append any unauthenticated apps not in the recommendations list.
		const rankedUnauthenticated3p = rankByRecommendations({
			items: unauthenticatedOptions,
			recommendedIds: recommended3pApps,
			idOf: (o) => o.value as string,
		});

		const { visibleOptions, overflowOptions } = applyAppPillLayout({
			layout,
			authenticatedOptions: rankedAuthenticatedOptions,
			rankedUnauthenticated3p,
		});

		// Apply "promoted" apps: any app that the user selected from the
		// "Show more apps" dialog is moved out of the overflow list and
		// rendered as a pill at the start of the visible options. Promoted
		// apps stay in the visible bar for the rest of the session, even if
		// this means rendering more than the regular max of 9 pills.
		//
		// Promoted apps are rendered in *reverse* selection order: the most
		// recently promoted app appears first. For example, if the user clicks
		// Google and then Confluence, the visible bar shows
		// [Confluence, Google, ...].
		if (promotedAppKeys.length === 0) {
			return { visibleOptions, overflowOptions };
		}

		const promotedSet = new Set<string>(promotedAppKeys);

		// Build the promoted pills in *reverse* promotion order so the most
		// recently selected app appears first. Look up the option from either
		// the visible or overflow lists so that promotion is idempotent and
		// works regardless of where the option currently sits.
		const allOptionsByKey = new Map<string, AppOption>();
		for (const opt of [...visibleOptions, ...overflowOptions]) {
			allOptionsByKey.set(String(opt.value), opt);
		}
		const promotedOptions = [...promotedAppKeys]
			.reverse()
			.map((key) => allOptionsByKey.get(key))
			.filter((o): o is AppOption => o !== undefined);

		const filteredVisibleOptions = visibleOptions.filter((o) => !promotedSet.has(String(o.value)));
		const filteredOverflowOptions = overflowOptions.filter(
			(o) => !promotedSet.has(String(o.value)),
		);

		return {
			visibleOptions: [...promotedOptions, ...filteredVisibleOptions],
			overflowOptions: filteredOverflowOptions,
		};
	}, [
		availableProducts,
		availableProductsForSelectedSites,
		availableNouns,
		layout,
		selectedProduct,
		selectedDatasource,
		selectedNounType,
		thirdPartyConfigs,
		products,
		formatMessage,
		recommended3pApps,
		promotedAppKeys,
		promotedWebCrawlerDatasource,
		primaryProduct,
		isRefreshedFilterRow,
	]);

	// Auto-promote any overflow option that is currently selected. This handles
	// the case where the page is loaded with a URL whose `selectedProduct` lives
	// inside the "Show more apps" overflow dialog: instead of leaving the app
	// hidden inside the dialog (with only the "Show all apps" trigger reflecting
	// the selection), we surface the selected app as the first visible pill.
	// Once promoted, the app stays in the visible bar for the rest of the
	// session even if the user later clicks a different pill — this matches the
	// existing in-memory promotion behaviour for apps clicked from the dialog.
	useEffect(() => {
		for (const option of result.overflowOptions) {
			if (option.isSelected && option.value !== undefined) {
				promoteApp(String(option.value));
			}
		}
	}, [result.overflowOptions, promoteApp]);

	return result;
}
